import React, { useState, useEffect, useMemo, useRef } from "react"
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  Chip,
  Avatar,
  ToggleButtonGroup,
  ToggleButton,
  IconButton,
  Tooltip,
} from "@mui/material"
import { Search, Email, CalendarToday, TableChart, Map as MapIcon, LockReset } from "@mui/icons-material"
import { GoogleMap, useLoadScript, InfoWindow } from "@react-google-maps/api"
import { fetchAllUsers, resetUserPassword } from "../services/adminService"
import { format } from "date-fns"
import PasswordResetModal from "../components/PasswordResetModal"

function AdminUsers() {
  const [users, setUsers] = useState([])
  const [filteredUsers, setFilteredUsers] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [searchQuery, setSearchQuery] = useState("")
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(25)
  const [viewMode, setViewMode] = useState("table") // "table" or "map"
  const [selectedUser, setSelectedUser] = useState(null)
  const [map, setMap] = useState(null)
  const [mapLoaded, setMapLoaded] = useState(false)
  const [resetPasswordModalOpen, setResetPasswordModalOpen] = useState(false)
  const [userToReset, setUserToReset] = useState(null)

  // Store markers and infoWindow references
  const markersRef = useRef([])
  const infoWindowRef = useRef(null)

  // Load Google Maps script
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
  })

  useEffect(() => {
    loadUsers()
  }, [])

  useEffect(() => {
    // Filter users based on search query
    if (searchQuery.trim() === "") {
      setFilteredUsers(users)
    } else {
      const query = searchQuery.toLowerCase()
      const filtered = users.filter(
        user =>
          (user.displayName && user.displayName.toLowerCase().includes(query)) ||
          (user.email && user.email.toLowerCase().includes(query)) ||
          (user.id && user.id.toLowerCase().includes(query)),
      )
      setFilteredUsers(filtered)
    }
    setPage(0) // Reset to first page when searching
  }, [searchQuery, users])

  const loadUsers = async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await fetchAllUsers()
      setUsers(data)
      setFilteredUsers(data)
    } catch (err) {
      console.error("Error loading users:", err)
      setError("Failed to load users. Please try again.")
    } finally {
      setLoading(false)
    }
  }

  const handleChangePage = (event, newPage) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = event => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  const handleViewModeChange = (event, newMode) => {
    if (newMode !== null) {
      setViewMode(newMode)
    }
  }

  const handleOpenResetPassword = user => {
    setUserToReset(user)
    setResetPasswordModalOpen(true)
  }

  const handleCloseResetPassword = () => {
    setResetPasswordModalOpen(false)
    setUserToReset(null)
  }

  const handleResetPassword = async (uid, newPassword) => {
    try {
      await resetUserPassword(uid, newPassword)
      // Success is handled by the modal
    } catch (error) {
      // Error is handled by the modal
      throw error
    }
  }

  // Get users with valid locations for map view
  const usersWithLocations = useMemo(() => {
    const withLocations = filteredUsers.filter(user => user.lastKnownLocation?.latitude && user.lastKnownLocation?.longitude)
    console.log("Total users:", filteredUsers.length)
    console.log("Users with locations:", withLocations.length)
    console.log("Sample user data:", filteredUsers[0])
    if (withLocations.length > 0) {
      console.log(
        "First 5 user locations:",
        withLocations.slice(0, 5).map(u => ({
          name: u.displayName,
          lat: u.lastKnownLocation.latitude,
          lng: u.lastKnownLocation.longitude,
        })),
      )
    }
    return withLocations
  }, [filteredUsers])

  // Create markers using native Google Maps API when map is loaded
  useEffect(() => {
    if (!map || !mapLoaded || !window.google) return

    // Clear existing markers
    markersRef.current.forEach(marker => marker.setMap(null))
    markersRef.current = []

    // Close existing infoWindow
    if (infoWindowRef.current) {
      infoWindowRef.current.close()
    }

    // Create a single shared InfoWindow
    if (!infoWindowRef.current) {
      infoWindowRef.current = new window.google.maps.InfoWindow()
    }

    // Create new markers
    usersWithLocations.forEach((user, index) => {
      const position = {
        lat: user.lastKnownLocation.latitude,
        lng: user.lastKnownLocation.longitude,
      }

      if (index === 0) {
        console.log(`Creating ${usersWithLocations.length} native markers`)
        console.log(`First marker for user:`, user.displayName, "at", position)
      }

      const marker = new window.google.maps.Marker({
        position,
        map,
      })

      marker.addListener("click", () => {
        const formatDate = dateVal => {
          if (!dateVal) return "N/A"
          const date = dateVal.toDate ? dateVal.toDate() : new Date(dateVal)
          return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
        }

        const formatDateTime = dateVal => {
          if (!dateVal) return "Never"
          const date = dateVal.toDate ? dateVal.toDate() : new Date(dateVal)
          return date.toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
            hour: "numeric",
            minute: "2-digit",
          })
        }

        const content = `
          <div style="padding: 8px; min-width: 200px; font-family: Arial, sans-serif;">
            <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 8px;">
              <img src="${user.photoURL || ""}" alt="" style="width: 32px; height: 32px; border-radius: 50%; background: #ccc;" onerror="this.style.display='none'"/>
              <div>
                <div style="font-weight: bold; font-size: 14px;">${user.displayName || "No Name"}</div>
                <div style="font-size: 12px; color: #666;">${user.email || ""}</div>
              </div>
            </div>
            <div style="font-size: 12px; color: #666;">
              <div><strong>Joined:</strong> ${formatDate(user.createdAt)}</div>
              <div><strong>Last Login:</strong> ${formatDateTime(user.lastLoginAt)}</div>
              ${user.lastKnownLocation?.updatedAt ? `<div><strong>Location Updated:</strong> ${formatDateTime(user.lastKnownLocation.updatedAt)}</div>` : ""}
            </div>
            <div style="margin-top: 8px;">
              <span style="display: inline-block; padding: 2px 8px; border-radius: 12px; font-size: 11px; background: ${user.emailVerified ? "#e8f5e9" : "#f5f5f5"}; color: ${user.emailVerified ? "#2e7d32" : "#666"};">
                ${user.emailVerified ? "Verified" : "Unverified"}
              </span>
            </div>
          </div>
        `

        infoWindowRef.current.setContent(content)
        infoWindowRef.current.open(map, marker)
      })

      markersRef.current.push(marker)
    })

    console.log(`Created ${markersRef.current.length} markers`)

    // Cleanup on unmount
    return () => {
      markersRef.current.forEach(marker => marker.setMap(null))
      markersRef.current = []
      if (infoWindowRef.current) {
        infoWindowRef.current.close()
      }
    }
  }, [map, mapLoaded, usersWithLocations])

  // Calculate map center based on user locations
  const mapCenter = useMemo(() => {
    if (usersWithLocations.length === 0) {
      return { lat: 39.8283, lng: -98.5795 } // Default to center of United States
    }
    const avgLat = usersWithLocations.reduce((sum, user) => sum + user.lastKnownLocation.latitude, 0) / usersWithLocations.length
    const avgLng = usersWithLocations.reduce((sum, user) => sum + user.lastKnownLocation.longitude, 0) / usersWithLocations.length
    return { lat: avgLat, lng: avgLng }
  }, [usersWithLocations])

  // Reset map loaded state when switching views
  useEffect(() => {
    if (viewMode !== "map") {
      setMapLoaded(false)
    }
  }, [viewMode])

  // Fit map bounds to show filtered markers, or center on single user
  useEffect(() => {
    if (!map || !window.google || usersWithLocations.length === 0) return

    // If there's only one user matching the search, center on them and open their popup
    if (usersWithLocations.length === 1 && searchQuery.trim() !== "") {
      const user = usersWithLocations[0]
      const position = {
        lat: user.lastKnownLocation.latitude,
        lng: user.lastKnownLocation.longitude,
      }
      map.setCenter(position)
      map.setZoom(12) // Zoom in close to the single user

      // Open the info window for this user
      if (infoWindowRef.current && markersRef.current.length > 0) {
        const formatDate = dateVal => {
          if (!dateVal) return "N/A"
          const date = dateVal.toDate ? dateVal.toDate() : new Date(dateVal)
          return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
        }

        const formatDateTime = dateVal => {
          if (!dateVal) return "Never"
          const date = dateVal.toDate ? dateVal.toDate() : new Date(dateVal)
          return date.toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
            hour: "numeric",
            minute: "2-digit",
          })
        }

        const content = `
          <div style="padding: 8px; min-width: 200px; font-family: Arial, sans-serif;">
            <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 8px;">
              <img src="${user.photoURL || ""}" alt="" style="width: 32px; height: 32px; border-radius: 50%; background: #ccc;" onerror="this.style.display='none'"/>
              <div>
                <div style="font-weight: bold; font-size: 14px;">${user.displayName || "No Name"}</div>
                <div style="font-size: 12px; color: #666;">${user.email || ""}</div>
              </div>
            </div>
            <div style="font-size: 12px; color: #666;">
              <div><strong>Joined:</strong> ${formatDate(user.createdAt)}</div>
              <div><strong>Last Login:</strong> ${formatDateTime(user.lastLoginAt)}</div>
              ${user.lastKnownLocation?.updatedAt ? `<div><strong>Location Updated:</strong> ${formatDateTime(user.lastKnownLocation.updatedAt)}</div>` : ""}
            </div>
            <div style="margin-top: 8px;">
              <span style="display: inline-block; padding: 2px 8px; border-radius: 12px; font-size: 11px; background: ${user.emailVerified ? "#e8f5e9" : "#f5f5f5"}; color: ${user.emailVerified ? "#2e7d32" : "#666"};">
                ${user.emailVerified ? "Verified" : "Unverified"}
              </span>
            </div>
          </div>
        `
        infoWindowRef.current.setContent(content)
        infoWindowRef.current.open(map, markersRef.current[0])
      }
    } else if (searchQuery.trim() !== "" && usersWithLocations.length > 1) {
      // Multiple matches - fit bounds to show all matching users
      const bounds = new window.google.maps.LatLngBounds()
      usersWithLocations.forEach(user => {
        bounds.extend({
          lat: user.lastKnownLocation.latitude,
          lng: user.lastKnownLocation.longitude,
        })
      })
      map.fitBounds(bounds)
    }
    // If no search query, keep the default US-centered view
  }, [map, usersWithLocations, searchQuery])

  if (loading || !isLoaded) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress size={60} />
        </Box>
      </Container>
    )
  }

  if (error || loadError) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">{error || "Error loading maps"}</Alert>
      </Container>
    )
  }

  const paginatedUsers = filteredUsers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box mb={4}>
        <Typography variant="h3" component="h1" gutterBottom fontWeight="bold">
          👥 Users Management
        </Typography>
        <Typography variant="h6" color="text.secondary">
          Total Users: {users.length.toLocaleString()}
        </Typography>
      </Box>

      {/* Search Bar and View Toggle */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" gap={2} alignItems="center">
            <TextField
              fullWidth
              placeholder="Search users by name, email, or ID..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
            <ToggleButtonGroup value={viewMode} exclusive onChange={handleViewModeChange} aria-label="view mode">
              <ToggleButton value="table" aria-label="table view">
                <TableChart sx={{ mr: 1 }} />
                Table
              </ToggleButton>
              <ToggleButton value="map" aria-label="map view">
                <MapIcon sx={{ mr: 1 }} />
                Map
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
          {viewMode === "map" && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
              Showing {usersWithLocations.length} of {filteredUsers.length} users with location data
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Table View */}
      {viewMode === "table" && (
        <Card>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>User</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Joined</TableCell>
                  <TableCell>Last Login</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedUsers.map(user => (
                  <TableRow key={user.id} hover>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={2}>
                        <Avatar src={user.photoURL} alt={user.displayName || user.email}>
                          {(user.displayName || user.email || "?")[0].toUpperCase()}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {user.displayName || "No Name"}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            ID: {user.id.substring(0, 8)}...
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <Email fontSize="small" color="action" />
                        <Typography variant="body2">{user.email || "No email"}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <CalendarToday fontSize="small" color="action" />
                        <Typography variant="body2">{user.createdAt ? format(user.createdAt, "MMM d, yyyy") : "N/A"}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {user.lastLoginAt ? format(user.lastLoginAt, "MMM d, yyyy 'at' h:mm a") : "Never"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={user.emailVerified ? "Verified" : "Unverified"} size="small" color={user.emailVerified ? "success" : "default"} />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Reset Password">
                        <IconButton size="small" color="primary" onClick={() => handleOpenResetPassword(user)}>
                          <LockReset />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={filteredUsers.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[10, 25, 50, 100]}
          />
        </Card>
      )}

      {/* Map View */}
      {viewMode === "map" && (
        <Card sx={{ height: "70vh" }}>
          <GoogleMap
            mapContainerStyle={{ width: "100%", height: "100%" }}
            center={mapCenter}
            zoom={5}
            options={{
              streetViewControl: false,
              mapTypeControl: true,
            }}
            onLoad={mapInstance => {
              console.log("Map loaded successfully")
              setMap(mapInstance)
              setMapLoaded(true)
            }}
          >
            {/* Markers are created via useEffect using native Google Maps API */}

            {selectedUser && (
              <InfoWindow
                position={{
                  lat: selectedUser.lastKnownLocation.latitude,
                  lng: selectedUser.lastKnownLocation.longitude,
                }}
                onCloseClick={() => setSelectedUser(null)}
              >
                <Box sx={{ p: 1, minWidth: 200 }}>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <Avatar src={selectedUser.photoURL} sx={{ width: 32, height: 32 }}>
                      {(selectedUser.displayName || selectedUser.email || "?")[0].toUpperCase()}
                    </Avatar>
                    <Box>
                      <Typography variant="subtitle2" fontWeight="bold">
                        {selectedUser.displayName || "No Name"}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {selectedUser.email}
                      </Typography>
                    </Box>
                  </Box>
                  <Typography variant="caption" display="block" color="text.secondary">
                    <strong>Joined:</strong> {selectedUser.createdAt ? format(selectedUser.createdAt, "MMM d, yyyy") : "N/A"}
                  </Typography>
                  <Typography variant="caption" display="block" color="text.secondary">
                    <strong>Last Login:</strong> {selectedUser.lastLoginAt ? format(selectedUser.lastLoginAt, "MMM d, yyyy 'at' h:mm a") : "Never"}
                  </Typography>
                  {selectedUser.lastKnownLocation?.updatedAt && (
                    <Typography variant="caption" display="block" color="text.secondary">
                      <strong>Location Updated:</strong>{" "}
                      {format(
                        selectedUser.lastKnownLocation.updatedAt.toDate
                          ? selectedUser.lastKnownLocation.updatedAt.toDate()
                          : new Date(selectedUser.lastKnownLocation.updatedAt),
                        "MMM d, yyyy 'at' h:mm a",
                      )}
                    </Typography>
                  )}
                  <Chip
                    label={selectedUser.emailVerified ? "Verified" : "Unverified"}
                    size="small"
                    color={selectedUser.emailVerified ? "success" : "default"}
                    sx={{ mt: 1 }}
                  />
                </Box>
              </InfoWindow>
            )}
          </GoogleMap>
        </Card>
      )}

      {/* Password Reset Modal */}
      <PasswordResetModal open={resetPasswordModalOpen} onClose={handleCloseResetPassword} user={userToReset} onResetPassword={handleResetPassword} />
    </Container>
  )
}

export default AdminUsers
